import { useCallback, useState } from "react";

import { toastModule } from "@/modules/toast";

import { NoteSourceArtifactInfo } from "./types";
import {
  DEFAULT_RETRANSCRIBE_ERROR_MESSAGE,
  getRetranscribeErrorMessageFromUnknown,
  retranscribeVoiceSource,
} from "./note-editor-source-bar-chip.utils";

type UseRetranscribeVoiceSourceArgs = {
  artifactInfo: NoteSourceArtifactInfo | null;
};

type UseRetranscribeVoiceSourceResult = {
  isRetranscribing: boolean;
  handleRetranscribe: () => Promise<void>;
};

const RETRANSCRIBE_UNAVAILABLE_MESSAGE = "Unable to retranscribe this voice source.";
const RETRANSCRIBE_SUCCESS_MESSAGE = "Transcript will refresh shortly.";
const SUCCESS_DELAY_MS = 4000;

export const useRetranscribeVoiceSource = ({
  artifactInfo,
}: UseRetranscribeVoiceSourceArgs): UseRetranscribeVoiceSourceResult => {
  const [isRetranscribing, setIsRetranscribing] = useState(false);

  const handleRetranscribe = useCallback(async () => {
    if (artifactInfo?.kind !== "AUDIO") return;

    const capturedAudioId = artifactInfo.data?.captured_audio_id ?? null;
    if (!capturedAudioId) {
      toastModule.triggerToast({ content: RETRANSCRIBE_UNAVAILABLE_MESSAGE });
      return;
    }

    setIsRetranscribing(true);
    let shouldDelayOnSuccess = false;

    try {
      const result = await retranscribeVoiceSource({ capturedAudioId });

      if (!result.success) {
        toastModule.triggerToast({ content: result.errorMessage });
      } else {
        shouldDelayOnSuccess = true;
      }
    } catch (error) {
      const errorMessage =
        getRetranscribeErrorMessageFromUnknown(error) || DEFAULT_RETRANSCRIBE_ERROR_MESSAGE;
      toastModule.triggerToast({ content: errorMessage });
    } finally {
      if (shouldDelayOnSuccess) {
        await new Promise(resolve => setTimeout(resolve, SUCCESS_DELAY_MS));
        toastModule.triggerToast({ content: RETRANSCRIBE_SUCCESS_MESSAGE });
      }

      setIsRetranscribing(false);
    }
  }, [artifactInfo]);

  return {
    isRetranscribing,
    handleRetranscribe,
  };
};
